import {
    formatReportCurrency,
    formatReportDisplayDate,
    getContainerTeusFromRow,
    getContainerTypeFromRow,
} from "@/utility/fclReportHelpers";
import {
    cell,
    col,
    currencyCol,
    percentCol,
    pickField,
    textCol,
} from "@/utility/fclReportFieldUtils";
import { EXPORT_CONTAINER_TEUS_COLUMNS } from "../export/exportBookingReportColumns";
import {
    clipText,
    dateReportCol,
    reportCol,
    textReportCol,
} from "./importReportColumnUtils";

export {
    IMPORT_BOOKING_COMPLEXITY_SUMMARY_COLUMNS,
    IMPORT_BOOKING_LIST_COLUMNS,
} from "./importBookingReportColumns";

export const IMPORT_CONTAINER_TEUS_COLUMNS = EXPORT_CONTAINER_TEUS_COLUMNS;

export const IMPORT_SALES_PERSON_TEUS_COLUMNS = [
    textCol("salesPerson", "SALES PERSON", [
        "salesPersonName",
        "SalesPersonName",
        "salesPerson",
        "SalesPerson",
    ]),
    col("teus", "TEUS", (row) => (
        <span className="font-semibold text-emerald-700 dark:text-emerald-400">
            {Number(pickField(row, ["teus", "Teus", "totalTeus"], 0)).toFixed(2)}
        </span>
    )),
];

export const IMPORT_CARRIER_TEUS_COLUMNS = [
    textCol("carrier", "CARRIER", ["carrierName", "CarrierName", "carrier", "Carrier"]),
    col("teus", "TEUS", (row) => (
        <span className="font-semibold text-emerald-700 dark:text-emerald-400">
            {Number(pickField(row, ["teus", "Teus", "totalTeus"], 0)).toFixed(2)}
        </span>
    )),
];

export {
    IMPORT_ACTIVE_SHIPMENT_COLUMNS,
    IMPORT_ACTIVE_SHIPMENT_LIST_COLUMNS,
} from "./importActiveShipmentReportColumns";

export {
    IMPORT_DEMURRAGE_COLUMNS,
    IMPORT_DEMURRAGE_LIST_COLUMNS,
} from "./importDemurrageReportColumns";

export const IMPORT_TRUCKING_COLUMNS = [
    textReportCol({
        key: "hblNo",
        label: "HBL #",
        width: 140,
        sticky: "left",
        fieldKeys: ["hblNo", "HblNo", "hbL_Number"],
    }),
    textReportCol({
        key: "carrierBookingNo",
        label: "CARRIER BOOKING #",
        width: 165,
        fieldKeys: ["carrierBookingNo", "CarrierBookingNo"],
    }),
    textReportCol({
        key: "carrierMBLNo",
        label: "CARRIER MBL #",
        width: 155,
        fieldKeys: ["carrierMBLNo", "CarrierMBLNo"],
    }),
    dateReportCol({ key: "dropDate", label: "DROP DATE", width: 130, fieldKeys: ["dropDate", "DropDate"] }),
    dateReportCol({ key: "pickupDate", label: "PICKUP DATE", width: 130, fieldKeys: ["pickupDate", "PickupDate"] }),
    dateReportCol({
        key: "liveLoadDate",
        label: "LIVE LOAD DATE",
        width: 140,
        fieldKeys: ["liveLoadDate", "LiveLoadDate"],
    }),
    textReportCol({
        key: "carrier",
        label: "CARRIER",
        width: 140,
        fieldKeys: ["carrier", "Carrier", "carrierName", "CarrierName"],
    }),
    textReportCol({ key: "shipper", label: "SHIPPER", width: 180, fieldKeys: ["shipper", "Shipper"] }),
    dateReportCol({ key: "ets", label: "ETS", width: 120, fieldKeys: ["ets", "ETS"] }),
    dateReportCol({ key: "eta", label: "ETA", width: 120, fieldKeys: ["eta", "ETA"] }),
    textReportCol({
        key: "placeOfReciept",
        label: "PLACE OF RECEIPT",
        width: 170,
        fieldKeys: ["placeOfReciept", "PlaceOfReciept", "placeOfReceipt", "PlaceOfReceipt"],
    }),
    textReportCol({
        key: "portOfLoading",
        label: "PORT OF LOADING",
        width: 170,
        fieldKeys: ["portOfLoading", "PortOfLoading", "portOfLoadingCustomized"],
    }),
    textReportCol({
        key: "portOfDischarge",
        label: "PORT OF DISCHARGE",
        width: 175,
        fieldKeys: ["portOfDischarge", "PortOfDischarge", "portOfDischargeCustomized"],
    }),
    textReportCol({ key: "bookedBy", label: "BOOKED BY", width: 140, fieldKeys: ["bookedBy", "BookedBy"] }),
];

const statusAuditTextCol = (key, label, fieldKeys, width = 175) =>
    textReportCol({ key, label, width, fieldKeys });

export const IMPORT_STATUS_AUDIT_COLUMNS = [
    statusAuditTextCol("refNo", "REF NO", ["refNo", "RefNo", "referenceNo", "ReferenceNo"], 145),
    statusAuditTextCol("bookingNo", "BOOKING NO.", ["bookingNo", "BookingNo"], 140),
    dateReportCol({ key: "ets", label: "ETS", width: 120, fieldKeys: ["ets", "ETS"] }),
    dateReportCol({ key: "eta", label: "ETA", width: 120, fieldKeys: ["eta", "ETA"] }),
    statusAuditTextCol("shippingInstructions", "SHIPPING INSTRUCTIONS", ["shippingInstructions", "ShippingInstructions"]),
    statusAuditTextCol("carrierBookingConfirmation", "CARRIER BOOKING CONFIRMATION", ["carrierBookingConfirmation", "CarrierBookingConfirmation"], 210),
    statusAuditTextCol("draftHBLIssued", "DRAFT HBL ISSUED", ["draftHBLIssued", "DraftHBLIssued"]),
    statusAuditTextCol("isfFilingCompleted", "ISF FILING COMPLETED", ["isfFilingCompleted", "IsfFilingCompleted"]),
    statusAuditTextCol("amshblFilingCompleted", "AMS HBL FILING COMPLETED", ["amshblFilingCompleted", "AmshblFilingCompleted"], 195),
    statusAuditTextCol("amshblRevised", "AMS HBL REVISED", ["amshblRevised", "AmshblRevised"]),
    statusAuditTextCol("telexOrOriginalHBLReceived", "TELEX OR ORIGINAL HBL RECEIVED", ["telexOrOriginalHBLReceived", "TelexOrOriginalHBLReceived"], 220),
    statusAuditTextCol("containerOnboard", "CONTAINER ONBOARD", ["containerOnboard", "ContainerOnboard"]),
    statusAuditTextCol("commercialDocumentsReceived", "COMMERCIAL DOCUMENTS RECEIVED", ["commercialDocumentsReceived", "CommercialDocumentsReceived"], 220),
    statusAuditTextCol("carrierMblReceived", "CARRIER MBL RECEIVED", ["carrierMblReceived", "CarrierMblReceived"]),
    statusAuditTextCol("preAlertSentToClient", "PRE-ALERT SENT TO CLIENT", ["preAlertSentToClient", "PreAlertSentToClient"], 195),
    statusAuditTextCol("arrivalNoticeReceivedFromCarrier", "ARRIVAL NOTICE RECEIVED FROM CARRIER", ["arrivalNoticeReceivedFromCarrier", "ArrivalNoticeReceivedFromCarrier"], 250),
    statusAuditTextCol("arrivalNoticeSent", "ARRIVAL NOTICE SENT", ["arrivalNoticeSent", "ArrivalNoticeSent"]),
    statusAuditTextCol("destinationChargesPaidToCarrier", "DESTINATION CHARGES PAID TO CARRIER", ["destinationChargesPaidToCarrier", "DestinationChargesPaidToCarrier"], 250),
    statusAuditTextCol("vesselArrival", "VESSEL ARRIVAL", ["vesselArrival", "VesselArrival"]),
    statusAuditTextCol("containersAvailable", "CONTAINERS AVAILABLE", ["containersAvailable", "ContainersAvailable"]),
    statusAuditTextCol("customsHoldExam", "CUSTOMS HOLD EXAM", ["customsHoldExam", "CustomsHoldExam"]),
    statusAuditTextCol("customsRelease", "CUSTOMS RELEASE", ["customsRelease", "CustomsRelease"]),
    statusAuditTextCol("carrierRelease", "CARRIER RELEASE", ["carrierRelease", "CarrierRelease"]),
    statusAuditTextCol("invoice", "INVOICE", ["invoice", "Invoice"], 120),
    statusAuditTextCol("deliveryOrderSent", "DELIVERY ORDER SENT", ["deliveryOrderSent", "DeliveryOrderSent"]),
    statusAuditTextCol("containerPickUp", "CONTAINER PICKUP", ["containerPickUp", "ContainerPickUp"]),
    statusAuditTextCol("deliveryDate", "DELIVERY DATE", ["deliveryDate", "DeliveryDate"]),
    statusAuditTextCol("emptyContainerReturn", "EMPTY CONTAINER RETURN", ["emptyContainerReturn", "EmptyContainerReturn"], 195),
    statusAuditTextCol("fileClosed", "FILE CLOSED", ["fileClosed", "FileClosed"]),
];

const getTrackingEventCount = (row) => {
    const events =
        row?.containerTrackingSummary ??
        row?.ContainerTrackingSummary ??
        row?.trackingEvents ??
        [];
    return Array.isArray(events) ? events.length : 0;
};

export const IMPORT_CONTAINER_TRACKING_COLUMNS = [
    textReportCol({
        key: "fileRefNo",
        label: "FILE REF #",
        width: 145,
        sticky: "left",
        fieldKeys: ["fileRefNo", "FileRefNo", "referenceNo", "ReferenceNo"],
    }),
    textReportCol({
        key: "mblBookingNo",
        label: "MBL / BOOKING #",
        width: 160,
        fieldKeys: ["mblBookingNo", "MBLBookingNo", "bookingNo", "BookingNo"],
    }),
    textReportCol({
        key: "carrier",
        label: "CARRIER",
        width: 140,
        fieldKeys: ["carrier", "Carrier", "carrierName", "CarrierName"],
    }),
    textReportCol({
        key: "containerNo",
        label: "CONTAINER #",
        width: 165,
        fieldKeys: ["containerNo", "ContainerNo", "containerNumber", "ContainerNumber"],
    }),
    textReportCol({
        key: "sizeType",
        label: "SIZE / TYPE",
        width: 120,
        fieldKeys: ["sizeType", "SizeType", "containerSize", "ContainerSize"],
    }),
    dateReportCol({ key: "ets", label: "ETS", width: 120, fieldKeys: ["ets", "ETS"] }),
    dateReportCol({ key: "eta", label: "ETA", width: 120, fieldKeys: ["eta", "ETA"] }),
    dateReportCol({ key: "revETA", label: "REV. ETA", width: 125, fieldKeys: ["revETA", "RevETA", "revisedETA", "RevisedETA"] }),
    textReportCol({
        key: "containerOnboard",
        label: "CONTAINER ONBOARD",
        width: 165,
        fieldKeys: ["containerOnboard", "ContainerOnboard"],
    }),
    textReportCol({
        key: "confirmArrival",
        label: "CONFIRM ARRIVAL",
        width: 150,
        fieldKeys: ["confirmArrival", "ConfirmArrival"],
    }),
    dateReportCol({ key: "trackingETS", label: "TRACKING ETS", width: 135, fieldKeys: ["trackingETS", "TrackingETS"] }),
    dateReportCol({ key: "trackingETA", label: "TRACKING ETA", width: 135, fieldKeys: ["trackingETA", "TrackingETA"] }),
    reportCol({
        key: "events",
        label: "EVENTS",
        width: 110,
        render: (row) => {
            const count = getTrackingEventCount(row);
            return count > 0 ? clipText(`${count} event(s)`) : clipText("-");
        },
        sortValue: (row) => getTrackingEventCount(row),
    }),
    textReportCol({ key: "shipper", label: "SHIPPER", width: 180, fieldKeys: ["shipper", "Shipper"] }),
    textReportCol({ key: "vessel", label: "VESSEL", width: 150, fieldKeys: ["vessel", "Vessel"] }),
    textReportCol({ key: "origin", label: "ORIGIN", width: 140, fieldKeys: ["origin", "Origin"] }),
    textReportCol({ key: "pol", label: "POL", width: 120, fieldKeys: ["pol", "POL"] }),
    textReportCol({ key: "pod", label: "POD", width: 120, fieldKeys: ["pod", "POD"] }),
    textReportCol({ key: "destination", label: "DESTINATION", width: 150, fieldKeys: ["destination", "Destination"] }),
    textReportCol({ key: "country", label: "COUNTRY", width: 120, fieldKeys: ["country", "Country"] }),
    textReportCol({
        key: "poNo",
        label: "PO #",
        width: 130,
        fieldKeys: ["poNo", "PoNo", "purchaseOrderNo", "PurchaseOrderNo"],
    }),
    textReportCol({ key: "bookedBy", label: "BOOKED BY", width: 140, fieldKeys: ["bookedBy", "BookedBy"] }),
    textReportCol({ key: "moduleType", label: "MODULE TYPE", width: 130, fieldKeys: ["moduleType", "ModuleType"] }),
];

export {
    IMPORT_ARRIVAL_NOTICE_COLUMNS,
    IMPORT_ARRIVAL_NOTICE_LIST_COLUMNS,
} from "./importArrivalNoticeReportColumns";

export {
    IMPORT_ETA_STATUS_COLUMNS,
    IMPORT_ETA_STATUS_LIST_COLUMNS,
} from "./importEtaStatusReportColumns";

export {
    IMPORT_FILE_CANCELLATION_COLUMNS,
    IMPORT_FILE_CANCELLATION_LIST_COLUMNS,
} from "./importFileCancellationReportColumns";

export {
    IMPORT_ARRIVAL_NOTICE_PRE_ALERT_COLUMNS,
    IMPORT_ARRIVAL_NOTICE_PRE_ALERT_LIST_COLUMNS,
} from "./importArrivalNoticePreAlertReportColumns";
